import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import type { OrganizationTaskTemplateRow } from "../organizationTaskTemplatesViewConfig";
import { getOrganizationTaskTemplatesTableMeta } from "../organizationTaskTemplatesTableMeta";

export const OrganizationTaskTemplateActionsCell = memo(
    function OrganizationTaskTemplateActionsCell({
        row,
        table,
    }: CellContext<ViewTableFeatures, OrganizationTaskTemplateRow, string>) {
        const meta = getOrganizationTaskTemplatesTableMeta(table);
        if (!meta) {
            return null;
        }

        const { callbacks } = meta;
        if (!callbacks) {
            return null;
        }

        const { kickDefaultOption } = row.original;
        if (kickDefaultOption !== undefined) {
            return (
                <ViewTableCell captureClicks>
                    <HStack gap={8} align="center" justify="end">
                        <Button
                            variant="primary"
                            size="sm"
                            className={viewTableStyles.showOnHover}
                            onClick={() =>
                                callbacks.onOverrideKickDefault(
                                    kickDefaultOption,
                                )
                            }
                        >
                            Override
                        </Button>
                    </HStack>
                </ViewTableCell>
            );
        }

        const { id } = row.original;
        const isArchived = meta.templatesById.get(id)?.archivedAt != null;
        const {
            onEditTask,
            onArchiveTemplate,
            onUnarchiveTemplate,
            onDetachTemplate,
            onDeleteTemplate,
        } = callbacks;
        const isUsedByServiceTemplates =
            (meta.serviceTemplateCounts[id] ?? 0) > 0;

        return (
            <ViewTableCell captureClicks>
                <HStack gap={8} align="center" justify="end">
                    <Button
                        variant="primary"
                        size="sm"
                        className={viewTableStyles.showOnHover}
                        onClick={() => onEditTask(id)}
                    >
                        Edit
                    </Button>
                    <Menu.More
                        className={viewTableStyles.showOnHover}
                        aria-label="Task template actions"
                    >
                        {isArchived ? (
                            <Menu.Item onClick={() => onUnarchiveTemplate(id)}>
                                Enable
                            </Menu.Item>
                        ) : (
                            <Menu.Item onClick={() => onArchiveTemplate(id)}>
                                Archive
                            </Menu.Item>
                        )}
                        {isUsedByServiceTemplates && (
                            <Menu.Item onClick={() => onDetachTemplate(id)}>
                                Detach
                            </Menu.Item>
                        )}
                        <Menu.Item
                            destructive
                            onClick={() => onDeleteTemplate(id)}
                        >
                            Delete
                        </Menu.Item>
                    </Menu.More>
                </HStack>
            </ViewTableCell>
        );
    },
);
